import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import { useTranslation } from 'react-i18next';
import {
    StyledCameraIcon,
    StyledColumnBox,
    StyledDeleteLink,
    StyledGreenButton,
    StyledIconButton,
    StyledImage,
    StyledImageBox,
    StyledMediumTypography,
    StyledModalButtons,
    StyledModalTitle,
    StyledNormalTypography,
    StyledRowBox,
    StyledWhiteButton
} from '../../theme/StyledElements';
import { useContext, useEffect, useRef, useState } from 'react';
import { MessagesDeProducteurs } from '../../models/MessagesDeProducteurs';
import { OutlinedInputComponent } from '../OutlinedInputComponent';
import { UserContext } from '../../providers/UserProvider';
import { saveMessage } from '../../services/cloudFirestore';
import { CircularProgress } from '@mui/material';
import cameraIcon from '../../assets/camera.svg';
import { getImageKitUrlFrom } from '../../utils';

const style = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    minWidth: 360,
    bgcolor: 'background.paper',
    border: '2px solid #134C03'
};

interface AddMessageModalProps {
    isModelOpen: boolean;
    setModelOpen: Function;
    onCancelClick: Function;
    onPublishClick: Function;
}

export function AddMessageModal({
    isModelOpen,
    setModelOpen,
    onCancelClick,
    onPublishClick
}: AddMessageModalProps): JSX.Element {
    const user = useContext(UserContext);
    const { t } = useTranslation();
    const [loading, setLoading] = useState<boolean>(false);
    const [disabled, setDisabled] = useState<boolean>(true);
    const [wordCount, setWordCount] = useState<number>(0);
    const [basketPhoto1, setBasketPhoto1] = useState<string>();
    const [basketPhoto2, setBasketPhoto2] = useState<string>();
    const basketPhoto1File = useRef<HTMLInputElement>(null);
    const basketPhoto2File = useRef<HTMLInputElement>(null);
    const [values, setValues] = useState<MessagesDeProducteurs>({
        idMessagesDeProducteur: undefined,
        idProducteur: undefined,
        titre: '',
        description: '',
        numberOfViews: undefined,
        photos: undefined,
        createdDate: undefined
    });

    useEffect(() => {
        if (
            values.titre &&
            values.description &&
            values.photos &&
            (values.photos[0] || values.photos[1])
        ) {
            setDisabled(false);
        } else {
            setDisabled(true);
        }
    }, [values]);

    const onTextFieldChange = (state: string, value: string | string[] | File[]): void => {
        setValues({ ...values, [state]: value });
    };

    const publishMessage = async (): Promise<void> => {
        setLoading(true);
        if (user && values && user.idProducteur) {
            const data = values;
            data.idProducteur = user.idProducteur;
            await saveMessage(data);
            onPublishClick();
            setLoading(false);
        }
    };

    const resetValues = (): void => {
        setBasketPhoto1('');
        setBasketPhoto2('');
        setValues({
            idMessagesDeProducteur: undefined,
            idProducteur: undefined,
            titre: '',
            description: '',
            numberOfViews: undefined,
            photos: undefined,
            createdDate: undefined
        });
        onCancelClick();
    };

    const triggerBasketPhoto1Upload = (): void => {
        if (basketPhoto1File && basketPhoto1File.current) {
            basketPhoto1File.current.click();
        }
    };

    const triggerBasketPhoto2Upload = (): void => {
        if (basketPhoto2File && basketPhoto2File.current) {
            basketPhoto2File.current.click();
        }
    };

    const updateBasketPhoto = (
        photoName: string,
        event: React.ChangeEvent<HTMLInputElement>
    ): void => {
        if (event.target.files) {
            const list = values.photos ?? [];
            if (photoName === 'basketPhoto1') {
                list[0] = event.target.files[0];
                setBasketPhoto1(URL.createObjectURL(event.target.files[0]));
            }
            if (photoName === 'basketPhoto2') {
                list[1] = event.target.files[0];
                setBasketPhoto2(URL.createObjectURL(event.target.files[0]));
            }
            onTextFieldChange('photos', list);
        }
    };

    const removeBasketPhoto = (photoName: string): void => {
        const list = values.photos ?? [];
        if (photoName === 'basketPhoto1') {
            list[0] = '';
            setBasketPhoto1('');
        }
        if (photoName === 'basketPhoto2') {
            list[1] = '';
            setBasketPhoto2('');
        }
        onTextFieldChange('photos', list);
    };

    const body = (
        <Box sx={style}>
            <StyledModalTitle>
                <Typography variant="body1" color="#FFFFFF">
                    {t('passMessage')}
                </Typography>
            </StyledModalTitle>
            <StyledColumnBox sx={{ margin: '40px' }}>
                <OutlinedInputComponent
                    isMultiline={false}
                    name="titre"
                    type="text"
                    label={t('titleMessage')}
                    value={values.titre}
                    onTextChange={(value: string): void => onTextFieldChange('titre', value)}
                    width="425px !important"
                />
                <OutlinedInputComponent
                    isMultiline
                    name="description"
                    type="text"
                    label={t('descriptionMessage')}
                    value={values.description}
                    onTextChange={(value: string): void => {
                        if (value.length <= 500) {
                            onTextFieldChange('description', value);
                            setWordCount(value.length);
                        }
                    }}
                    width="425px !important"
                />
                <StyledNormalTypography
                    variant="caption"
                    color="#394A35"
                    sx={{ marginBottom: '10px' }}>
                    {t('wordCount500', { count: wordCount })}
                </StyledNormalTypography>
                <StyledRowBox>
                    <StyledImageBox sx={{ border: basketPhoto1 ? 'none' : '1px solid #394A35' }}>
                        {!basketPhoto1 && (
                            <div>
                                <input
                                    type="file"
                                    ref={basketPhoto1File}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                                        updateBasketPhoto('basketPhoto1', event)
                                    }
                                    accept="image/*"
                                    multiple={false}
                                    style={{ display: 'none' }}
                                />
                                <StyledIconButton
                                    onClick={triggerBasketPhoto1Upload}
                                    sx={{ flexDirection: 'column' }}>
                                    <StyledCameraIcon src={cameraIcon} alt="imageUpload" />
                                    <StyledMediumTypography variant="caption">
                                        {t('addPicture')}
                                    </StyledMediumTypography>
                                </StyledIconButton>
                            </div>
                        )}
                        {basketPhoto1 && (
                            <div>
                                <StyledImage
                                    src={getImageKitUrlFrom(basketPhoto1)}
                                    alt="imageUpload"
                                />
                                <StyledDeleteLink
                                    type="button"
                                    onClick={(): void => {
                                        removeBasketPhoto('basketPhoto1');
                                    }}
                                    sx={{ marginBottom: '18px' }}>
                                    <Typography
                                        variant="overline"
                                        sx={{
                                            textTransform: 'none',
                                            letterSpacing: 0,
                                            fontWeight: 500
                                        }}>
                                        {t('deletePhoto')}
                                    </Typography>
                                </StyledDeleteLink>
                            </div>
                        )}
                    </StyledImageBox>
                    <StyledImageBox sx={{ border: basketPhoto2 ? 'none' : '1px solid #394A35' }}>
                        {!basketPhoto2 && (
                            <div>
                                <input
                                    type="file"
                                    ref={basketPhoto2File}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                                        updateBasketPhoto('basketPhoto2', event)
                                    }
                                    accept="image/*"
                                    multiple={false}
                                    style={{ display: 'none' }}
                                />
                                <StyledIconButton
                                    onClick={triggerBasketPhoto2Upload}
                                    sx={{ flexDirection: 'column' }}>
                                    <StyledCameraIcon src={cameraIcon} alt="imageUpload" />
                                    <StyledMediumTypography variant="caption">
                                        {t('addPicture')}
                                    </StyledMediumTypography>
                                </StyledIconButton>
                            </div>
                        )}
                        {basketPhoto2 && (
                            <div>
                                <StyledImage
                                    src={getImageKitUrlFrom(basketPhoto2)}
                                    alt="imageUpload"
                                />
                                <StyledDeleteLink
                                    type="button"
                                    onClick={(): void => {
                                        removeBasketPhoto('basketPhoto2');
                                    }}
                                    sx={{ marginBottom: '18px' }}>
                                    <Typography
                                        variant="overline"
                                        sx={{
                                            textTransform: 'none',
                                            letterSpacing: 0,
                                            fontWeight: 500
                                        }}>
                                        {t('deletePhoto')}
                                    </Typography>
                                </StyledDeleteLink>
                            </div>
                        )}
                    </StyledImageBox>
                </StyledRowBox>
            </StyledColumnBox>
            <StyledModalButtons>
                <StyledWhiteButton onClick={(): void => resetValues()}>
                    <StyledMediumTypography variant="caption">{t('cancel')}</StyledMediumTypography>
                </StyledWhiteButton>
                {loading && (
                    <CircularProgress
                        style={{
                            color: '#134C03',
                            height: '25px',
                            width: '25px',
                            marginTop: '25px'
                        }}
                    />
                )}
                {!loading && (
                    <StyledGreenButton
                        disabled={disabled}
                        sx={{ opacity: disabled ? 0.5 : 1 }}
                        onClick={(): Promise<void> => publishMessage()}>
                        <StyledMediumTypography variant="caption" color="#FFFFFF">
                            {t('publish')}
                        </StyledMediumTypography>
                    </StyledGreenButton>
                )}
            </StyledModalButtons>
        </Box>
    );

    return (
        <Modal
            open={isModelOpen}
            onClose={(): void => {
                setModelOpen(false);
            }}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description">
            {body}
        </Modal>
    );
}
